<template>
  <div :class="'policy'">
    <section class="policy-select">
      <AttensiCheckbox
        :checked="policy.value !== undefined"
        :name="`toggle-product-policy-checkbox-${policy.id}`"
        :data-cy="`toggle-product-policy-checkbox-${policy.id}`"
        class="policy-select__checkbox"
        @toggle="() => handleChange(undefined)"
      >
        {{ policy.name }}
      </AttensiCheckbox>

      <BaseChip v-if="isBaseContentPolicy" theme="portal-blue">
        {{ $t('product.POLICY_MY_ENTIRE_COMPANY') }}
      </BaseChip>

      <PolicyOpenButton
        :policy-id="policy.id"
        :data-cy="`open-product-policy-button-${policy.id}`"
      />
    </section>

    <section class="policy-options">
      <ul class="policy-options__list">
        <!-- EXPLORE -->
        <li
          :class="[
            'policy-options__item',
            { 'policy-options__item--active': isExplore },
            { 'policy-options__item--disabled': isExploreDisabled },
          ]"
          @click="() => handleChange('none')"
        >
          <div class="policy-options__item-label">
            <div class="policy-options__item-label-text">
              {{ $t('product.POLICY_EXPLORE_ONLY') }}
            </div>
            <div @click.stop>
              <BaseTooltip interactive class="policy-options__item-tooltip">
                <template #trigger>
                  <img
                    src="@/assets/img/help.svg"
                    alt="help"
                    class="policy-options__item-tooltip-icon"
                  />
                </template>
                <template #content>
                  <div class="policy-options__item-toolip-content">
                    <div class="policy-options__item-toolip-content-title">
                      {{ $t('product.POLICY_EXPLORE_ONLY') }}
                    </div>
                    <div
                      v-sanitize-html="
                        $t('product.POLICY_EXPLORE_ONLY_TOOLTIP_TEXT')
                      "
                      class="policy-options__item-toolip-content-paragraph"
                    ></div>
                  </div>
                </template>
              </BaseTooltip>
            </div>
          </div>
          <AttensiToggle
            manual-mode
            size="small"
            class="policy-options__item-toggle"
            :toggled="isExplore"
            :disabled="isExploreDisabled"
            :name="`toggle-product-policy-checkbox-${policy.id}`"
            :data-cy="`mandatory-checkbox-${policy.id}`"
          />
        </li>
        <!-- MANDATORY -->
        <li
          :class="[
            'policy-options__item',
            { 'policy-options__item--active': isMandatory },
            { 'policy-options__item--disabled': isMandatoryDisabled },
          ]"
          @click="() => handleChange('mandatory')"
        >
          <div class="policy-options__item-label">
            <div class="policy-options__item-label-text">
              {{ $t('product.POLICY_MANDATORY') }}
            </div>
          </div>
          <AttensiToggle
            manual-mode
            size="small"
            class="policy-options__item-toggle"
            :toggled="isMandatory"
            :disabled="isMandatoryDisabled"
            :name="`toggle-product-policy-checkbox-${policy.id}`"
            :data-cy="`mandatory-checkbox-${policy.id}`"
          />
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup lang="ts">
/**
 * !!NOTE!!
 * We want to change this component considerably more soon.
 * We want to make the controls more intuitive, user-friendly and semantic.
 */
import BaseChip from '@/components/generics/base/BaseChip.vue'
import BaseTooltip from '@/components/generics/base/BaseTooltip.vue'
import PolicyOpenButton from '@/components/policies/PolicyOpenButton.vue'
import { AttensiCheckbox, AttensiToggle } from '@attensi/materia'
import { computed } from 'vue'

type PolicyOption = {
  id: string
  name: string
  value: undefined | 'optional' | 'mandatory' | 'none'
}
const { policy, isBaseContentPolicy } = defineProps<{
  policy: PolicyOption
  isBaseContentPolicy: boolean
}>()

const emit = defineEmits(['onAddOrEditPolicy', 'onRemovePolicy', 'onChange'])

const isMandatory = computed(() => policy.value === 'mandatory')
const isExplore = computed(() => policy.value === 'none')
const isMandatoryDisabled = computed(() => isExplore.value)
const isExploreDisabled = computed(() => isMandatory.value)

// This will in effect handle the change as a "Switch" or "Tab options"
const handleChange = (value) => {
  const newValue = policy.value === value ? 'optional' : value
  emit('onChange', { ...policy, value: newValue })
}
</script>

<style lang="scss" scoped>
.policy {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $spacing-xs 0;
}
.policy-select {
  display: flex;
  align-items: center;
  gap: 10px;
}
.policy-options {
  display: flex;
  flex-direction: column;

  &__header {
    @include micro-header;
    color: $color-text-light;
    margin-bottom: $spacing-3xs;
  }
  &__list {
    display: flex;
    gap: $spacing-m;
    justify-content: space-between;
    width: 100%;
  }
  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: $spacing-2xs;
    font-weight: 400;
    cursor: pointer;
  }
  &__item-label {
    display: flex;
    align-items: center;
  }
  &__item-label-text {
    @include disable-text-selection;
    white-space: nowrap;
  }
  &__item-tooltip {
    margin-left: $spacing-5xs;
  }
  &__item-tooltip-icon {
    width: $spacing-2xs;
    height: auto;
  }
  &__item-toolip-content {
    cursor: default;
    font-weight: 400;
    font-size: $font-size-m;
    padding: $spacing-3xs;
  }
  &__item-toolip-content-title {
    @include subtitle-2;
    margin-bottom: $spacing-4xs;
    color: $color-text-light;
  }
  &__item-toolip-content-paragraph {
    margin-bottom: $spacing-3xs;
    line-height: 1.4;
    font-size: $font-size-s;
    color: $color-text;
  }

  // Modifiers:
  &__item--active {
    font-weight: 700;
  }
  &__item--disabled {
    cursor: default;
  }
  &__item--disabled &__item-label-text {
    color: $color-text-light;
  }
}

@media screen and (max-width: $mobile-width-max) {
  .policy {
    flex-direction: column;
  }
  .policy-select {
    display: flex;
    align-items: center;
    width: 100%;
    margin-bottom: $spacing-2xs;
    border-left: none;
  }
  .policy-options {
    display: flex;
    align-items: flex-start;
    width: 100%;
    border-left: none;
    padding-left: 0;
    padding-top: $spacing-2xs;
  }
}
</style>
